import React, { useRef, useEffect, useCallback, useState } from 'react';
import mapboxgl, { Map } from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { Box, Slider, Typography } from '@mui/material';
import { useAppState } from 'src/state-management/state-provider';
import { mapBoxConfig } from 'src/mapbox-helpers/config';
import { fitMapToBoundaries } from 'src/mapbox-helpers/map-layer-utils';
import { addSiteBoundaryWithColor } from './map-helper';
import { getColorByIndex } from 'src/mapbox-helpers/color-utils';
import MapboxFeature from 'src/mapbox-helpers/MapboxFeature';
import { bbox } from '@turf/turf';
import { roundToTwoDecimalPlaces } from 'src/util/util';
import clientService from 'src/clientService';

interface MapComponentProps {
  onOpenDataPanel?: () => void;
}

const MapComponent: React.FC<MapComponentProps> = ({ onOpenDataPanel }) => {
  const mapContainer = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<Map | null>(null);
  const { state, dispatch } = useAppState();
  const { bioMapData } = state.selectedProject;
  const { selectedSite, selectedLocation, mapOpacity } = state.bioInteractiveMapFilter;
  const [tooltip, setTooltip] = useState({
    show: false,
    x: 0,
    y: 0,
    content: '',
  });

  const filterStateRef = useRef(state.bioInteractiveMapFilter);
  useEffect(() => {
    filterStateRef.current = state.bioInteractiveMapFilter;
  }, [state.bioInteractiveMapFilter]);

  const projectIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    projectIdRef.current = state.selectedProject?.projectDetails?.project_id;
  }, [state.selectedProject?.projectDetails?.project_id]);

  const fetchMarkersTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchAndSetMarkers = useCallback(async (map: Map, projectId: string) => {
    if (!map) return;
    const bounds = map.getBounds();
    if (!bounds) return;
    const bboxParam = `${bounds.getWest()},${bounds.getSouth()},${bounds.getEast()},${bounds.getNorth()}`;

    try {
      const response = await clientService.request<any[], any>({
        path: `/api/v1/projects/${projectId}/map/markers`,
        method: 'GET',
        query: { bbox: bboxParam },
        secure: true,
      });

      const markers = response.data || [];
      const features = markers.map((marker) => ({
        type: 'Feature' as const,
        geometry: {
          type: 'Point' as const,
          coordinates: [marker.longitude, marker.latitude],
        },
        properties: {
          deployment_id: marker.deployment_id,
          location_id: marker.location_id,
          deployment_name: marker.deployment_name,
          status: marker.status,
          device_type: marker.device_type,
        },
      }));

      const source = map.getSource('deployment-markers') as mapboxgl.GeoJSONSource;
      if (source) {
        source.setData({
          type: 'FeatureCollection',
          features: features,
        });
      }
    } catch (e) {
      console.error('Error fetching map markers:', e);
    }
  }, []);

  const triggerFetchMarkers = useCallback((map: Map, projectId: string) => {
    if (fetchMarkersTimeoutRef.current) {
      clearTimeout(fetchMarkersTimeoutRef.current);
    }
    fetchMarkersTimeoutRef.current = setTimeout(() => {
      fetchAndSetMarkers(map, projectId);
    }, 300);
  }, [fetchAndSetMarkers]);

  const onMapLoad = useCallback(() => {
    if (!mapRef.current || !bioMapData) return;
    const map = mapRef.current;

    if (bioMapData.sites) {
      bioMapData.sites.forEach((site, index) => {
        addSiteBoundaryWithColor(map, site, getColorByIndex(index));
        const layerId = `fill-site-boundary-${site.site_id}`;
        map.on('mouseenter', layerId, (e) => {
          if (e.features && e.features.length > 0) {
            const properties = e.features[0].properties;
            const content = `
              <div>
                <strong>${properties?.Name}</strong><br/>
                ${roundToTwoDecimalPlaces(properties?.Area)} ha
              </div>
            `;
            setTooltip({
              show: true,
              x: e.point.x,
              y: e.point.y,
              content,
            });
          }
        });
        map.on('mouseleave', layerId, () => {
          setTooltip({ show: false, x: 0, y: 0, content: '' });
        });
      });
    }

    // Add deployment markers source with clustering enabled
    map.addSource('deployment-markers', {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
      cluster: true,
      clusterMaxZoom: 14,
      clusterRadius: 50,
    });

    // Add cluster circle layer
    map.addLayer({
      id: 'clusters',
      type: 'circle',
      source: 'deployment-markers',
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': [
          'step',
          ['get', 'point_count'],
          '#51bbd6',
          10,
          '#f1f075',
          30,
          '#f28cb1',
        ],
        'circle-radius': [
          'step',
          ['get', 'point_count'],
          20,
          10,
          30,
          30,
          40,
        ],
        'circle-stroke-width': 1,
        'circle-stroke-color': '#fff',
      },
    });

    // Add cluster count labels layer
    map.addLayer({
      id: 'cluster-count',
      type: 'symbol',
      source: 'deployment-markers',
      filter: ['has', 'point_count'],
      layout: {
        'text-field': '{point_count}',
        'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
        'text-size': 12,
      },
    });

    // Add unclustered point layer
    map.addLayer({
      id: 'unclustered-point',
      type: 'circle',
      source: 'deployment-markers',
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-color': [
          'match',
          ['get', 'device_type'],
          'Audio',
          '#00f',
          'Camera',
          '#0f0',
          'Hydro',
          '#f00',
          '#11b4da',
        ],
        'circle-radius': 8,
        'circle-stroke-width': 1,
        'circle-stroke-color': '#fff',
      },
    });

    // Handle cluster expansions on click
    map.on('click', 'clusters', (e) => {
      const features = map.queryRenderedFeatures(e.point, {
        layers: ['clusters'],
      });
      if (!features.length) return;
      const clusterId = features[0].properties?.cluster_id;
      const source = map.getSource('deployment-markers') as mapboxgl.GeoJSONSource;
      source.getClusterExpansionZoom(clusterId, (err, zoom) => {
        if (err || zoom === undefined || zoom === null) return;
        const coordinates = (features[0].geometry as any).coordinates;
        map.easeTo({
          center: coordinates,
          zoom: zoom,
        });
      });
    });

    // Handle marker click to open data panel
    map.on('click', 'unclustered-point', (e) => {
      const features = map.queryRenderedFeatures(e.point, {
        layers: ['unclustered-point'],
      });
      if (features.length === 0) return;
      const properties = features[0].properties;
      if (!properties) return;

      const deploymentId = properties.deployment_id;
      const deploymentName = properties.deployment_name;
      const deviceType = properties.device_type;

      dispatch({
        type: 'SET_BIO_INTERACTIVE_MAP_FILTER',
        payload: {
          ...filterStateRef.current,
          selectedDeployment: {
            deployment_id: deploymentId,
            deployment_name: deploymentName,
            device_type: deviceType,
          } as any,
        },
      });

      if (onOpenDataPanel) {
        onOpenDataPanel();
      }
    });

    // Pointer cursors on hover
    map.on('mouseenter', 'clusters', () => {
      map.getCanvas().style.cursor = 'pointer';
    });
    map.on('mouseleave', 'clusters', () => {
      map.getCanvas().style.cursor = '';
    });
    map.on('mouseenter', 'unclustered-point', () => {
      map.getCanvas().style.cursor = 'pointer';
    });
    map.on('mouseleave', 'unclustered-point', () => {
      map.getCanvas().style.cursor = '';
    });

    // Wire moveend boundary fetching
    map.on('moveend', () => {
      const pId = projectIdRef.current;
      if (pId) {
        triggerFetchMarkers(map, pId);
      }
    });

    // Load initial markers
    const pId = projectIdRef.current;
    if (pId) {
      triggerFetchMarkers(map, pId);
    }

    if (bioMapData.sites && bioMapData.sites.length > 0) {
      fitMapToBoundaries(map, bioMapData.sites);
    }
  }, [bioMapData, triggerFetchMarkers, onOpenDataPanel, dispatch]);

  useEffect(() => {
    if (mapRef.current || !mapContainer.current) return;

    mapRef.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/satellite-v9',
      center: [mapBoxConfig.defaulView.lng, mapBoxConfig.defaulView.lat],
      zoom: mapBoxConfig.defaulView.zoom,
    });

    mapRef.current.addControl(new mapboxgl.NavigationControl());

    mapRef.current.on('load', onMapLoad);

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [onMapLoad]);

  useEffect(() => {
    if (!mapRef.current) return;

    if (!selectedSite) {
      if (bioMapData?.sites) {
        fitMapToBoundaries(mapRef.current, bioMapData.sites);
      }
      return;
    }

    const siteToFocus = bioMapData?.sites?.find((s) => s.site_id === selectedSite.site_id);

    if (siteToFocus?.boundary) {
      const tempFeature = new MapboxFeature();
      tempFeature.add(siteToFocus.boundary, {});
      const geoJsonBoundary = tempFeature.toJSON();

      if (geoJsonBoundary && geoJsonBoundary.features.length > 0) {
        try {
          const calculatedBbox = bbox(geoJsonBoundary);
          mapRef.current.fitBounds(calculatedBbox as [number, number, number, number], {
            padding: 50,
            animate: true,
            duration: 1500,
          });
        } catch (e) {
          console.error('Error calculating bounding box for single site:', e);
        }
      }
    }
  }, [selectedSite, bioMapData?.sites]);

  useEffect(() => {
    if (!mapRef.current) return;
    const map = mapRef.current;

    // Trigger boundary fetch on site/location filter changes
    const pId = projectIdRef.current;
    if (pId) {
      triggerFetchMarkers(map, pId);
    }

    bioMapData?.sites?.forEach((site) => {
      const layerId = `fill-site-boundary-${site.site_id}`;
      if (map.getLayer(layerId)) {
        const opacity =
          selectedSite && selectedSite.site_id !== site.site_id
            ? 0.2
            : mapBoxConfig.polygon.defaultPolygonOpacity;
        map.setPaintProperty(layerId, 'fill-opacity', opacity);
      }
    });
  }, [selectedSite, selectedLocation, bioMapData?.sites, triggerFetchMarkers]);

  useEffect(() => {
    if (!mapRef.current) return;

    if (selectedLocation) {
      const locationToFocus = bioMapData?.locations?.find(
        (l) => l.location_id === selectedLocation.location_id,
      );
      if (locationToFocus?.location) {
        try {
          const feature = MapboxFeature.parseFeatureFromString(locationToFocus.location, {});
          if (feature && feature.geometry.type === 'Point') {
            const coordinates = feature.geometry.coordinates as [number, number];
            mapRef.current.flyTo({
              center: coordinates,
              zoom: 17,
              essential: true,
            });
          }
        } catch (e) {
          console.error('Error parsing location', e);
        }
      }
    } else if (selectedSite) {
      const siteToFocus = bioMapData?.sites?.find((s) => s.site_id === selectedSite.site_id);
      if (siteToFocus) {
        fitMapToBoundaries(mapRef.current, [siteToFocus]);
      }
    }
  }, [selectedLocation, bioMapData?.locations, selectedSite, bioMapData?.sites]);

  useEffect(() => {
    if (!mapRef.current || !bioMapData) return;
    const map = mapRef.current;

    bioMapData.sites?.forEach((site) => {
      const layerId = `fill-site-boundary-${site.site_id}`;
      if (map.getLayer(layerId)) {
        map.setPaintProperty(layerId, 'fill-opacity', mapOpacity);
      }
    });
  }, [mapOpacity, bioMapData?.sites, bioMapData]);

  const handleOpacityChange = (_event: Event, newValue: number | number[]) => {
    dispatch({
      type: 'SET_MAP_OPACITY',
      payload: newValue as number,
    });
  };

  return (
    <Box
      ref={mapContainer}
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
      }}
    >
      {tooltip.show && (
        <div
          style={{
            position: 'absolute',
            left: tooltip.x,
            top: tooltip.y,
            transform: 'translate(-50%, -100%)',
            backgroundColor: 'white',
            padding: '5px',
            borderRadius: '3px',
            boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
            zIndex: 1000,
            pointerEvents: 'none',
          }}
          dangerouslySetInnerHTML={{ __html: tooltip.content }}
        />
      )}
      <Box
        sx={{
          position: 'absolute',
          top: 10,
          right: 50,
          zIndex: 1000,
          backgroundColor: mapBoxConfig.mapUIElementBackgroundColor,
          padding: '10px',
          borderRadius: '5px',
          width: 200,
        }}
      >
        <Typography id="opacity-slider" gutterBottom>
          Layer Opacity
        </Typography>
        <Slider
          aria-labelledby="opacity-slider"
          value={mapOpacity}
          onChange={handleOpacityChange}
          min={0.1}
          max={1.0}
          step={0.1}
          valueLabelDisplay="auto"
        />
      </Box>
    </Box>
  );
};

export default MapComponent;